import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { authApi } from '../api/client'
import { useAuth } from '../context/AuthContext'

const schema = z.object({
  userId: z.string().min(1, '아이디를 입력해주세요'),
  password: z.string().min(1, '비밀번호를 입력해주세요'),
})
type FormData = z.infer<typeof schema>

export default function LoginPage() {
  const navigate = useNavigate()
  const { login } = useAuth()
  const [error, setError] = useState('')

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  const onSubmit = async (data: FormData) => {
    setError('')
    try {
      const res = await authApi.login(data)
      login(res.token, { userId: res.userId, name: res.name, email: res.email, userLevel: res.userLevel ?? 'FREE_TRIAL', admin: res.admin ?? false })
      navigate('/dashboard', { replace: true })
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } }
      setError(err.response?.data?.error ?? '로그인에 실패했습니다.')
    }
  }

  const handleNaverLogin = async () => {
    try {
      const url = await authApi.getNaverLoginUrl()
      window.location.href = url
    } catch {
      setError('네이버 로그인 연동에 실패했습니다.')
    }
  }

  return (
    <div className="min-h-screen flex flex-col-reverse lg:flex-row">
      {/* 좌측 패널 - 브랜딩 */}
      <div className="flex lg:w-1/2 bg-slate-900 flex-col justify-between gap-y-8 p-6 sm:p-8 xl:p-12 relative overflow-hidden">
        {/* 배경 그라디언트 효과 */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0b1120] via-slate-900 to-[#0e1a33]" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-500/5 rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl" />

        {/* 그리드 패턴 */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '40px 40px',
          }}
        />

        <div className="hidden lg:block relative z-10">

        </div>

        {/* 중앙 텍스트 */}
        <div className="relative z-10 space-y-5 sm:space-y-6 lg:p-8 lg:rounded-2xl lg:bg-white/[0.03] lg:border lg:border-white/10 lg:backdrop-blur-sm">
          <div className="hidden lg:block space-y-2">
            <h2 className="text-2xl sm:text-3xl xl:text-4xl font-bold text-white leading-tight">
              3D 공간 데이터를<br />웹에서 바로 활용하세요
            </h2>
          </div>
          <p className="text-slate-400 text-base leading-relaxed">
            대용량 3D 데이터를 3D Tiles로 변환하고<br />
            웹 브라우저에서 바로 확인하세요.
          </p>

          {/* 기능 목록 + 샘플 버튼 (모바일은 나란히, lg 이상은 세로 배치) */}
          <div className="flex items-center justify-between gap-4 lg:block">
            <ul className="space-y-3.5 pt-4">
              {['3D 데이터 자동 변환', '3D Tiles 웹 뷰어', '변환 완료 이메일 알림'].map((item) => (
                <li key={item} className="flex items-center gap-3 text-slate-300 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <a
              href="https://mrs.gaia3d.com/sample/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 shrink-0 lg:mt-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white text-sm font-semibold rounded-lg transition-colors backdrop-blur-sm"
            >
              View Sample
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          </div>
        </div>


        {/* 하단 */}
        <div className="relative z-10 flex flex-wrap items-center gap-x-3 gap-y-1">
          {/* 로고 */}
          <div className="relative z-10">
            <img src="/gaia3d-logo.png" alt="Gaia3D" className="h-10 object-contain object-left" />
          </div>
          <p className="text-slate-500 text-xs">© 2010 Gaia3D, Inc. All rights reserved.</p>
          <span className="text-slate-700 text-xs lg:hidden">·</span>
          <Link to="/terms" className="lg:hidden text-slate-400 text-xs hover:text-slate-200 transition-colors">
            서비스 이용약관
          </Link>
        </div>
      </div>

      {/* 우측 패널 - 로그인 폼 */}
      <div className="w-full flex-1 lg:w-1/2 flex flex-col justify-center items-center bg-white px-4 py-8 sm:px-6 sm:py-12">
        <div className="w-full max-w-md">
          <div className="mb-10 sm:mb-14">
            <span className="text-3xl sm:text-4xl font-bold tracking-tight text-blue-900">
              <span className="font-semibold">mago</span>Reality Service
            </span>
          </div>
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl font-bold text-slate-900">로그인</h1>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">아이디</label>
              <input
                {...register('userId')}
                className="w-full px-3.5 py-2.5 border border-slate-300 rounded-lg text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                placeholder="아이디를 입력하세요"
              />
              {errors.userId && <p className="mt-1 text-xs text-red-600">{errors.userId.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">비밀번호</label>
              <input
                {...register('password')}
                type="password"
                className="w-full px-3.5 py-2.5 border border-slate-300 rounded-lg text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                placeholder="비밀번호를 입력하세요"
              />
              {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              {isSubmitting ? '로그인 중...' : '로그인'}
            </button>

            <div className="relative flex items-center gap-3">
              <div className="flex-1 border-t border-slate-200" />
              <span className="text-xs text-slate-400">또는</span>
              <div className="flex-1 border-t border-slate-200" />
            </div>

            <button
              type="button"
              onClick={handleNaverLogin}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-[#03C75A] hover:bg-[#02b350] text-white text-sm font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            >
              <span className="font-extrabold text-base leading-none">N</span>
              네이버로 로그인
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-500">
            계정이 없으신가요?{' '}
            <Link to="/register" className="font-semibold text-blue-600 hover:text-blue-700">
              회원가입
            </Link>
          </p>

          <div className="mt-6 pt-6 border-t border-slate-100 text-center hidden lg:block">
            <Link to="/terms" className="text-xs text-slate-400 hover:text-slate-600 transition-colors">
              서비스 이용약관
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
